import useDataQuery from '@hooks/use-data-query';
import { IDocumentFileModel, IDocumentFolderModel, IExternalLinkModel } from '@api/models';

import produce from 'immer';

import { useCallback, useMemo } from 'react';
import { getAllDocumentFolders, getAllDocuments, getAllExternalLinks } from '@api/services/document.service';
// import { getAllDocuments } from '@api/services/box-storage.service';

const useDocumentListData = (clientId: string) => {
    // const documentCategoryListQuery = useDataQuery(['documentCategories'], () => getAllDocumentCategories(), {
    //     enabled: Boolean(hasProPlan),
    // });

    const documentFolderListQuery = useDataQuery(['documentFolders'], () => getAllDocumentFolders(clientId), {
        enabled: Boolean(clientId),
    });

    const documentFileListQuery = useDataQuery(['documentFiles'], () => getAllDocuments(clientId), {
        enabled: Boolean(clientId),
    });

    const externalLinkListQuery = useDataQuery(['externalLinks'], () => getAllExternalLinks(clientId), {
        enabled: Boolean(clientId),
    });

    /* =================================================================================================
     * Folder
     * ===============================================================================================*/

    const folders = useMemo(() => {
        if (documentFolderListQuery.isLoading) return [];

        return documentFolderListQuery.data;
    }, [documentFolderListQuery.isLoading, documentFolderListQuery.data]);

    /* =================================================================================================
     * Files
     * ===============================================================================================*/

    const files = useMemo(() => {
        if (documentFileListQuery.isLoading) return [];

        return documentFileListQuery.data;
    }, [documentFileListQuery.isLoading, documentFileListQuery.data]);

    /* =================================================================================================
     * External Links
     * ===============================================================================================*/

    const externalLinks = useMemo(() => {
        if (externalLinkListQuery.isLoading) return [];

        return externalLinkListQuery.data;
    }, [externalLinkListQuery.isLoading, externalLinkListQuery.data]);

    /* =================================================================================================
     * Folder
     * ===============================================================================================*/

    const addFolder = useCallback(
        (folder: IDocumentFolderModel) => {
            documentFolderListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    draftList.push(folder);

                    return draftList;
                });
            });
        },
        [documentFolderListQuery]
    );

    const updateFolder = useCallback(
        (folderId: string, updatedFolder: IDocumentFolderModel) => {
            documentFolderListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    const index = draftList.findIndex((row) => row._id === folderId);
                    if (index === -1) return draftList;

                    draftList[index] = updatedFolder;

                    return draftList;
                });
            });
        },
        [documentFolderListQuery]
    );

    const deleteFolder = useCallback(
        (folderId: string) => {
            documentFolderListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    const index = draftList.findIndex((row) => row._id === folderId);

                    if (index !== -1) {
                        draftList.splice(index, 1);
                    }

                    return draftList;
                });
            });
        },
        [documentFolderListQuery]
    );

    /* =================================================================================================
     * File
     * ===============================================================================================*/

    const addFile = useCallback(
        (file: IDocumentFileModel) => {
            documentFileListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    draftList.push(file);

                    return draftList;
                });
            });
        },
        [documentFileListQuery]
    );

    const updateFile = useCallback(
        (fileId: string, updatedFile: IDocumentFileModel) => {
            documentFileListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    const index = draftList.findIndex((row) => row._id === fileId);

                    if (index === -1) return draftList;

                    draftList[index] = updatedFile;

                    return draftList;
                });
            });
        },
        [documentFileListQuery]
    );

    const deleteFile = useCallback(
        (fileId: string) => {
            documentFileListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    const index = draftList.findIndex((row) => row._id === fileId);

                    if (index !== -1) {
                        draftList.splice(index, 1);
                    }

                    return draftList;
                });
            });
        },
        [documentFileListQuery]
    );

    /* =================================================================================================
     * External Links
     * ===============================================================================================*/

    const addExternalLink = useCallback(
        (externalLink: IExternalLinkModel) => {
            externalLinkListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    draftList.push(externalLink);
                    return draftList;
                });
            });
        },
        [externalLinkListQuery]
    );

    const updateExternalLink = useCallback(
        (externalLinkId: string, updatedExternalLink: IExternalLinkModel) => {
            externalLinkListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    const index = draftList.findIndex((row) => row._id === externalLinkId);

                    if (index === -1) return draftList;

                    draftList[index] = updatedExternalLink;

                    return draftList;
                });
            });
        },
        [externalLinkListQuery]
    );

    const deleteExternalLink = useCallback(
        (fileId: string) => {
            externalLinkListQuery.setData((prevList) => {
                return produce(prevList, (draftList) => {
                    const index = draftList.findIndex((row) => row._id === fileId);

                    if (index !== -1) {
                        draftList.splice(index, 1);
                    }

                    return draftList;
                });
            });
        },
        [externalLinkListQuery]
    );

    const isQueryLoading = useCallback((query: { isLoading: boolean; fetchStatus: string }) => {
        return query.isLoading && query.fetchStatus !== 'idle';
    }, []);

    const refetchAll = useCallback(() => {
        documentFolderListQuery.refetch();
        documentFileListQuery.refetch();
        externalLinkListQuery.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        isLoading:
            isQueryLoading(documentFileListQuery) ||
            isQueryLoading(documentFolderListQuery) ||
            isQueryLoading(externalLinkListQuery),
        folders,
        files,
        externalLinks,
        addFolder,
        updateFolder,
        deleteFolder,
        addFile,
        updateFile,
        deleteFile,
        addExternalLink,
        updateExternalLink,
        deleteExternalLink,
        refetchAll,
    };
};

export default useDocumentListData;
